import { solarShortProductName } from './solarProductName';
import {
  type SolarElectricalTopology,
  type SolarIdentifiedPanel,
} from './solarElectricalTopology';
import {
  checkSolarTopologyString,
  type SolarStringSpecKey,
} from './solarStringCheck';

/**
 * Single-line electrical diagram for a solar design, computed from the saved
 * electrical topology. Pure data: the designer draws `shapes` as JSX, and
 * `solarSingleLineDiagramSvg` turns the same shapes into standalone SVG markup
 * (download now, estimate documents later). Never throws.
 *
 * Layout is left to right: strings (DC) → DC isolator → inverter MPPT inputs →
 * AC isolator → generation meter (UK) → consumer unit / main service panel →
 * grid meter → grid.
 */

export type SolarDiagramRegion = 'uk' | 'us';

/** The catalogue fields the diagram reads. GraphQL product specs fit as-is. */
export type SolarDiagramProduct = {
  applianceId: string;
  name: string;
  category?: string | null;
  acOutputKw?: number | null;
  phases?: number | null;
  hybrid?: boolean | null;
  mpptCount?: number | null;
  maxInputVoltageV?: number | null;
  maxModuleIscA?: number | null;
  inputsPerMppt?: number | null;
  maxMpptIscA?: number | null;
  minMpptVoltageV?: number | null;
  panelCapacityW?: number | null;
  openCircuitVoltageV?: number | null;
  shortCircuitCurrentA?: number | null;
  vocTemperatureCoefficientPctPerC?: number | null;
  usableCapacityKwh?: number | null;
  maxChargeDischargeKw?: number | null;
  integratedInverter?: boolean | null;
  pvConversion?: {
    acOutputKw?: number | null;
    mpptCount?: number | null;
  } | null;
};

export type SolarSingleLineDiagramInput = {
  region: SolarDiagramRegion;
  topology: SolarElectricalTopology;
  panels: readonly SolarIdentifiedPanel[];
  /** Display name per roof, e.g. "Roof 1 South". */
  roofs: readonly { faceId: number; label: string }[];
  excludedFaceIds?: readonly number[];
  /** Inverters, panels and batteries; saved spec snapshots win over these. */
  products: readonly SolarDiagramProduct[];
  /** Each string's colour by string ID, as the roof view and Strings list draw it. */
  stringColours?: ReadonlyMap<string, string>;
  /** The design's selected battery, if any. */
  battery?: { product: SolarDiagramProduct; quantity?: number | null } | null;
  /** Site design low (°C), see siteDesignMinTemperatureC. */
  siteMinTemperatureC?: number | null;
};

export type SolarDiagramTone = 'default' | 'muted' | 'ok' | 'warn';

type Paint = {
  tone?: SolarDiagramTone;
  /** 'none' (default), 'current' for the tone colour, or a literal colour. */
  fill?: string;
  fillOpacity?: number;
  strokeWidth?: number;
  dashed?: boolean;
  /** False draws fill only. */
  stroke?: boolean;
};

export type SolarDiagramShape =
  | ({
      type: 'line';
      x1: number;
      y1: number;
      x2: number;
      y2: number;
    } & Paint)
  | ({
      type: 'rect';
      x: number;
      y: number;
      width: number;
      height: number;
      rx?: number;
    } & Paint)
  | ({ type: 'circle'; cx: number; cy: number; r: number } & Paint)
  | ({ type: 'path'; d: string } & Paint)
  | ({ type: 'polyline'; points: [number, number][] } & Paint)
  | {
      type: 'text';
      x: number;
      y: number;
      text: string;
      size: 12 | 13;
      weight?: 'normal' | 'bold';
      anchor?: 'start' | 'middle' | 'end';
      tone?: SolarDiagramTone;
    };

export type SolarDiagramStringCheck =
  | 'ok'
  | 'check'
  | 'unknown'
  | 'review'
  | 'unconnected';

export type SolarAcBreaker = {
  /** Inverter (or battery) continuous AC output current, P / (V × phase factor). */
  currentA: number;
  /** UK: the current itself; US: 125 % of it. */
  designCurrentA: number;
  /** Smallest standard size at or above the design current; null when above the range. */
  breakerA: number | null;
  voltageV: number;
  phases: 1 | 3;
};

export type SolarDiagramNodeKind =
  | 'string'
  | 'battery'
  | 'dcIsolator'
  | 'inverter'
  | 'isolator'
  | 'generationMeter'
  | 'consumerUnit'
  | 'gridMeter'
  | 'grid';

export type SolarDiagramNode = {
  id: string;
  kind: SolarDiagramNodeKind;
  x: number;
  y: number;
  width: number;
  height: number;
  /** Text lines as drawn, first line is the title. */
  lines: string[];
  /** Topology id (string, inverter instance) or product id (battery). */
  sourceId?: string;
  /** String nodes. */
  colour?: string;
  panelCount?: number;
  check?: SolarDiagramStringCheck;
  checkReasons?: string[];
  /** Inverter and AC battery nodes. */
  acKw?: number | null;
  breaker?: SolarAcBreaker | null;
  /** Battery nodes. */
  coupling?: 'dc' | 'ac';
  /** False when the battery's coupling is not established by its catalogue row. */
  couplingVerified?: boolean;
  usableKwh?: number | null;
};

export type SolarDiagramEdge = {
  id: string;
  from: string;
  to: string;
  kind: 'dc' | 'ac';
  points: [number, number][];
  /** "MPPT 1 · DC 1" on DC inputs, the AC current on inverter outputs. */
  label: string | null;
};

export type SolarSingleLineDiagram = {
  region: SolarDiagramRegion;
  width: number;
  height: number;
  nodes: SolarDiagramNode[];
  edges: SolarDiagramEdge[];
  /** Draw in order: edges first, then symbols and text. */
  shapes: SolarDiagramShape[];
  totals: {
    inverters: number;
    strings: number;
    unconnectedStrings: number;
    panels: number;
    dcKw: number | null;
    acKw: number | null;
    batteries: number;
    batteryKwh: number | null;
  };
  /** "3 inverters, 8 strings, 80 panels". */
  summary: string;
};

// ── Breaker sizing ─────────────────────────────────────────────────────────

/**
 * UK: BS EN 60898 preferred MCB ratings, sized In ≥ Ib (BS 7671 433.1.1).
 * US: NEC 240.6(A) standard ratings up to 200 A, sized ≥ 125 % of the
 * inverter's continuous output current (NEC 690.9(B), 705.30).
 */
export const SOLAR_AC_BREAKER_SIZES_A: Record<SolarDiagramRegion, number[]> = {
  uk: [16, 20, 25, 32, 40, 50, 63],
  us: [
    15, 20, 25, 30, 35, 40, 45, 50, 60, 70, 80, 90, 100, 110, 125, 150, 175,
    200,
  ],
};

/**
 * The US service a product name declares: "480/277V" and "208/120 V" are
 * three-phase wye, read as the line-to-line voltage; a bare 480 V is
 * three-phase too.
 */
const usSupplyFromName = (name: string | null | undefined) => {
  const match = /(\d{3})(?:\s*\/\s*(\d{3}))?\s*V(?:AC)?\b/i.exec(name ?? '');
  const volts = match ? Number(match[1]) : null;
  if (!volts || ![208, 240, 277, 480].includes(volts)) return null;
  return { volts, threePhase: !!match?.[2] || volts === 480 };
};

/** Nominal line voltage for the region and phase count. */
export const solarAcVoltage = (
  region: SolarDiagramRegion,
  phases: 1 | 3,
  productName?: string | null
) => {
  if (region === 'uk') return phases === 3 ? 400 : 230;
  const named = usSupplyFromName(productName)?.volts;
  if (phases === 3) return named && named !== 240 ? named : 208;
  return 240;
};

export const solarAcBreaker = (input: {
  region: SolarDiagramRegion;
  acKw: number | null | undefined;
  phases?: number | null;
  productName?: string | null;
}): SolarAcBreaker | null => {
  const { region, acKw } = input;
  if (typeof acKw !== 'number' || !Number.isFinite(acKw) || acKw <= 0)
    return null;
  const phases: 1 | 3 =
    input.phases === 3 ||
    (region === 'us' && usSupplyFromName(input.productName)?.threePhase)
      ? 3
      : 1;
  const voltageV = solarAcVoltage(region, phases, input.productName);
  const currentA = round(
    (acKw * 1000) / (voltageV * (phases === 3 ? Math.sqrt(3) : 1)),
    2
  );
  const designCurrentA = round(region === 'us' ? currentA * 1.25 : currentA, 2);
  const breakerA =
    SOLAR_AC_BREAKER_SIZES_A[region].find((size) => size >= designCurrentA) ??
    null;
  return { currentA, designCurrentA, breakerA, voltageV, phases };
};

// ── Formatting ─────────────────────────────────────────────────────────────

const round = (value: number, digits: number) =>
  Math.round(value * 10 ** digits) / 10 ** digits;

const known = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

const fmt = (value: number, digits = 1) =>
  Number(value.toFixed(digits)).toString();

const plural = (count: number, one: string, many = `${one}s`) =>
  `${count} ${count === 1 ? one : many}`;

/** Rough advance width so labels can be clipped to their column. */
const textWidth = (text: string, size: number, bold = false) =>
  text.length * size * (bold ? 0.58 : 0.53);

const fit = (text: string, width: number, size: number, bold = false) => {
  if (textWidth(text, size, bold) <= width) return text;
  const chars = Math.max(
    1,
    Math.floor(width / (size * (bold ? 0.58 : 0.53))) - 1
  );
  return `${text.slice(0, chars).trimEnd()}…`;
};

// ── Layout constants ───────────────────────────────────────────────────────

const PAD = 16;
const ROW_H = 56;
const GROUP_GAP = 28;
const STRING_X = PAD;
const STRING_W = 264;
const STRING_RIGHT = STRING_X + STRING_W;
/** DC isolator column between the strings and the inverter MPPT labels. */
const DC_ISO_X = STRING_RIGHT + 44;
const INV_X = STRING_RIGHT + 168;
const INV_W = 208;
const INV_RIGHT = INV_X + INV_W;
const INV_MIN_H = 96;
const AC_BATTERY_H = 84;
const STEP = 100;
const CU_W = 168;
const FOOTER_H = 40;
/** US diagrams carry a second footer line. */
const FOOTER_NOTE_H = 18;
const DC_ISOLATOR_LABEL = 'DC isolator (may be integrated)';
/** NEC 705.12: the load-side connection must fit the panel busbar. */
const US_BUSBAR_NOTE = 'Check busbar rating (NEC 705.12)';

// ── Builder ────────────────────────────────────────────────────────────────

type TopologyString = SolarElectricalTopology['strings'][number];
type TopologyUnit = SolarElectricalTopology['inverterInstances'][number];

export const buildSolarSingleLineDiagram = (
  input: SolarSingleLineDiagramInput
): SolarSingleLineDiagram => {
  const { region, topology, stringColours } = input;
  const excluded = new Set(input.excludedFaceIds ?? []);
  const productById = new Map(
    input.products.map((product) => [product.applianceId, product])
  );
  const selectionById = new Map(
    topology.equipmentSelections.map((item) => [item.id, item])
  );
  const panelById = new Map(input.panels.map((panel) => [panel.id, panel]));
  const roofLabel = new Map(
    input.roofs.map((roof) => [roof.faceId, roof.label])
  );

  const productOf = (selectionId: string | null | undefined) =>
    productById.get(selectionById.get(selectionId ?? '')?.applianceId ?? '');
  /** Saved snapshot first, then the live catalogue. */
  const spec = <K extends keyof SolarDiagramProduct>(
    selectionId: string | null | undefined,
    key: K
  ): SolarDiagramProduct[K] | undefined => {
    const specs = selectionById.get(selectionId ?? '')?.specSnapshot?.specs;
    const saved =
      specs && typeof specs === 'object' && !Array.isArray(specs)
        ? (specs as Record<string, unknown>)[key]
        : undefined;
    if (saved !== undefined && saved !== null)
      return saved as SolarDiagramProduct[K];
    return productOf(selectionId)?.[key];
  };
  const nameOf = (selectionId: string | null | undefined) => {
    const saved = selectionById.get(selectionId ?? '')?.specSnapshot?.name;
    return typeof saved === 'string'
      ? saved
      : (productOf(selectionId)?.name ?? null);
  };
  const numberSpec = (
    selectionId: string | null | undefined,
    key: keyof SolarDiagramProduct
  ) => {
    const value = spec(selectionId, key);
    return known(value) ? value : null;
  };
  /** Any finite number, negative coefficients included. */
  const rawNumber = (
    selectionId: string | null | undefined,
    key: SolarStringSpecKey
  ) => {
    const value = spec(selectionId, key);
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  };
  const countryCode = region === 'us' ? 'US' : 'GB';

  const nodes: SolarDiagramNode[] = [];
  const edges: SolarDiagramEdge[] = [];
  const shapes: SolarDiagramShape[] = [];
  const edgeShapes: SolarDiagramShape[] = [];

  // ── Strings ──
  const activePanels = (string: TopologyString) =>
    string.panelIds.flatMap((id) => {
      const panel = panelById.get(id);
      return panel && panel.included && !excluded.has(panel.faceId)
        ? [panel]
        : [];
    });
  const unitIds = new Set(topology.inverterInstances.map((unit) => unit.id));
  const isOptimiser = (unit: TopologyUnit) => {
    const name = nameOf(unit.equipmentSelectionId) ?? '';
    return (
      /solaredge|optimi[sz]/i.test(name) &&
      !spec(unit.equipmentSelectionId, 'hybrid') &&
      spec(unit.equipmentSelectionId, 'mpptCount') == null
    );
  };
  const stringInfo = (string: TopologyString) => {
    const index = topology.strings.indexOf(string);
    const members = activePanels(string);
    const first = members[0] ?? panelById.get(string.panelIds[0]);
    const watts = numberSpec(first?.equipmentSelectionId, 'panelCapacityW');
    const mixed = members.some(
      (panel) =>
        numberSpec(panel.equipmentSelectionId, 'panelCapacityW') !== watts
    );
    const faces = [...new Set(members.map((panel) => panel.faceId))];
    let roof = 'No panels';
    if (faces.length > 1) roof = `${faces.length} roofs`;
    else if (faces.length === 1)
      roof = roofLabel.get(faces[0]) ?? `Roof ${faces[0]}`;
    const unit = topology.inverterInstances.find(
      (item) => item.id === string.inverterInstanceId
    );
    let check: SolarDiagramStringCheck;
    let reasons: string[] = [];
    if (!unit || !string.mpptKey || !string.inputKey) check = 'unconnected';
    else if (string.incompleteReason) check = 'review';
    else if (isOptimiser(unit)) check = 'unknown';
    else {
      const result = checkSolarTopologyString({
        string,
        topology,
        panels: input.panels,
        excludedFaceIds: input.excludedFaceIds,
        specOf: rawNumber,
        siteMinTemperatureC: input.siteMinTemperatureC,
        countryCode,
      });
      check = result.status;
      reasons = result.reasons;
    }
    return {
      index,
      count: members.length,
      watts: mixed ? null : watts,
      dcKw: members.every((panel) =>
        known(numberSpec(panel.equipmentSelectionId, 'panelCapacityW'))
      )
        ? members.reduce(
            (sum, panel) =>
              sum +
              (numberSpec(panel.equipmentSelectionId, 'panelCapacityW') ?? 0),
            0
          ) / 1000
        : null,
      roof,
      check,
      reasons,
      colour: stringColours?.get(string.id),
    };
  };

  /** Port order: tracker order, then input order on the unit. */
  const portRank = (unit: TopologyUnit, string: TopologyString) => {
    let rank = 0;
    for (const tracker of unit.trackers)
      for (const input of tracker.inputs) {
        if (tracker.key === string.mpptKey && input.key === string.inputKey)
          return rank;
        rank++;
      }
    return rank;
  };

  // ── Battery coupling ──
  const battery = input.battery ?? null;
  const batteryQty =
    battery && known(battery.quantity) ? Math.round(battery.quantity) : 1;
  /** Combined battery/PV-conversion units carry their own storage on DC. */
  const combinedUnit = (unit: TopologyUnit) =>
    !!spec(unit.equipmentSelectionId, 'pvConversion') ||
    spec(unit.equipmentSelectionId, 'category') === 'BATTERY';
  const batteryUnitIds = new Set(
    topology.inverterInstances.filter(combinedUnit).map((unit) => unit.id)
  );
  const batteryIsUnit =
    !!battery &&
    topology.inverterInstances.some(
      (unit) =>
        selectionById.get(unit.equipmentSelectionId)?.applianceId ===
        battery.product.applianceId
    );
  /**
   * DC side only for a battery without its own inverter
   * (integratedInverter false) and a hybrid unit to take it; AC only for one
   * with its own inverter (true). Anything else keeps the old placement
   * (hybrid unit DC, else AC) marked "coupling unverified".
   */
  const integrated = battery?.product.integratedInverter;
  const anyHybrid =
    battery && !batteryIsUnit && battery.product.category !== 'INVERTER'
      ? topology.inverterInstances.find(
          (unit) =>
            !batteryUnitIds.has(unit.id) &&
            spec(unit.equipmentSelectionId, 'hybrid') === true
        )
      : undefined;
  const hybridUnit = integrated === true ? undefined : anyHybrid;
  const couplingVerified =
    (integrated === false && !!hybridUnit) || integrated === true;
  const acBattery =
    battery &&
    !batteryIsUnit &&
    !hybridUnit &&
    battery.product.category !== 'INVERTER'
      ? battery.product
      : null;
  const UNVERIFIED = 'Coupling unverified';

  // ── Groups: one per inverter unit, then unconnected strings ──
  type Row =
    | { kind: 'string'; string: TopologyString }
    | {
        kind: 'battery';
        id: string;
        name: string | null;
        usableKwh: number | null;
        quantity: number;
      };
  const groups: { unit: TopologyUnit | null; rows: Row[] }[] = [];
  for (const unit of topology.inverterInstances) {
    const rows: Row[] = topology.strings
      .filter((string) => string.inverterInstanceId === unit.id)
      .sort((a, b) => portRank(unit, a) - portRank(unit, b))
      .map((string) => ({ kind: 'string' as const, string }));
    if (batteryUnitIds.has(unit.id)) {
      const kwh = numberSpec(unit.equipmentSelectionId, 'usableCapacityKwh');
      rows.push({
        kind: 'battery',
        id: `battery:${unit.id}`,
        name: null,
        usableKwh: kwh,
        quantity: 1,
      });
    } else if (hybridUnit?.id === unit.id && battery) {
      rows.push({
        kind: 'battery',
        id: `battery:${unit.id}`,
        name: battery.product.name,
        usableKwh: known(battery.product.usableCapacityKwh)
          ? battery.product.usableCapacityKwh * batteryQty
          : null,
        quantity: batteryQty,
      });
    }
    groups.push({ unit, rows });
  }
  const unconnected = topology.strings.filter(
    (string) =>
      !string.inverterInstanceId || !unitIds.has(string.inverterInstanceId)
  );
  if (unconnected.length)
    groups.push({
      unit: null,
      rows: unconnected.map((string) => ({ kind: 'string' as const, string })),
    });

  // ── Column x positions ──
  const isoX = INV_RIGHT + 64;
  const genX = isoX + STEP;
  const cuX = (region === 'uk' ? genX : isoX) + 64;
  const cuRight = cuX + CU_W;
  const meterX = cuRight + 64;
  const gridX = meterX + STEP;
  const width = gridX + 56 + PAD;

  const totals = {
    inverters: topology.inverterInstances.length,
    strings: topology.strings.length,
    unconnectedStrings: unconnected.length,
    panels: 0,
    dcKw: 0 as number | null,
    acKw: 0 as number | null,
    batteries: 0,
    batteryKwh: null as number | null,
  };
  const addBatteryKwh = (kwh: number | null) => {
    totals.batteries++;
    if (kwh != null) totals.batteryKwh = (totals.batteryKwh ?? 0) + kwh;
  };

  /** AC branches entering the consumer unit, top to bottom. */
  const branches: {
    sourceId: string;
    y: number;
    breaker: SolarAcBreaker | null;
    generation: boolean;
    title: string;
  }[] = [];

  let y = PAD + 8;
  for (const group of groups) {
    const unit = group.unit;
    const rowsH = group.rows.length * ROW_H;
    const groupH = unit ? Math.max(rowsH, INV_MIN_H) : rowsH;
    const rowTop = y + (groupH - rowsH) / 2;
    const unitNodeId = unit ? `inverter:${unit.id}` : null;
    const stringRows = group.rows.filter((row) => row.kind === 'string');
    if (unit && stringRows.length) {
      const firstY = rowTop + group.rows.indexOf(stringRows[0]) * ROW_H;
      const lastY =
        rowTop + (group.rows.indexOf(stringRows.at(-1)!) + 1) * ROW_H;
      nodes.push({
        id: `dc-isolator:${unit.id}`,
        kind: 'dcIsolator',
        x: DC_ISO_X - 20,
        y: firstY,
        width: 40,
        height: lastY - firstY,
        lines: [DC_ISOLATOR_LABEL],
        sourceId: unit.id,
      });
      // Above the first string, clear of the MPPT labels and the inverter box.
      shapes.push({
        type: 'text',
        x: DC_ISO_X,
        y: firstY - 6,
        text: DC_ISOLATOR_LABEL,
        size: 12,
        anchor: 'middle',
        tone: 'muted',
      });
    }

    group.rows.forEach((row, index) => {
      const cy = rowTop + index * ROW_H + ROW_H / 2;
      if (row.kind === 'string') {
        const info = stringInfo(row.string);
        totals.panels += info.count;
        if (info.dcKw == null) totals.dcKw = null;
        else if (totals.dcKw != null) totals.dcKw += info.dcKw;
        const nodeId = `string:${row.string.id}`;
        const title = `${row.string.name} · ${info.count} × ${
          info.watts != null ? `${fmt(info.watts, 0)} W` : 'panels'
        }`;
        const badge = badgeOf(info.check);
        const badgeW = badge ? badge.width : 0;
        const textX = STRING_X + 46;
        const textRoom = STRING_RIGHT - textX - badgeW - 8;
        const lines = [
          fit(title, textRoom, 13, true),
          fit(info.roof, textRoom, 12),
        ];
        nodes.push({
          id: nodeId,
          kind: 'string',
          x: STRING_X,
          y: cy - ROW_H / 2 + 4,
          width: STRING_W,
          height: ROW_H - 8,
          lines,
          sourceId: row.string.id,
          colour: info.colour,
          panelCount: info.count,
          check: info.check,
          checkReasons: info.reasons,
        });
        moduleSymbol(shapes, STRING_X, cy, info.colour, !unit);
        shapes.push(
          {
            type: 'text',
            x: textX,
            y: cy - 4,
            text: lines[0],
            size: 13,
            weight: 'bold',
          },
          {
            type: 'text',
            x: textX,
            y: cy + 13,
            text: lines[1],
            size: 12,
            tone: 'muted',
          }
        );
        if (badge) badgeShapes(shapes, STRING_RIGHT - badge.width, cy, badge);
        if (unit && unitNodeId) {
          const label = [row.string.mpptKey, row.string.inputKey]
            .filter(Boolean)
            .join(' · ');
          const points: [number, number][] = [
            [STRING_RIGHT + 6, cy],
            [INV_X, cy],
          ];
          edges.push({
            id: `dc:${row.string.id}`,
            from: nodeId,
            to: unitNodeId,
            kind: 'dc',
            points,
            label: label || null,
          });
          // BS 7671 712.537 / NEC 690.15: a DC isolator before the inverter.
          edgeShapes.push(
            {
              type: 'polyline',
              points: [
                [STRING_RIGHT + 6, cy],
                [DC_ISO_X - 20, cy],
              ],
              strokeWidth: 1.5,
            },
            {
              type: 'polyline',
              points: [
                [DC_ISO_X + 20, cy],
                [INV_X, cy],
              ],
              strokeWidth: 1.5,
            }
          );
          isolatorSymbol(shapes, DC_ISO_X, cy, 1.5);
          if (label)
            shapes.push({
              type: 'text',
              x: INV_X - 8,
              y: cy - 6,
              text: label,
              size: 12,
              anchor: 'end',
              tone: 'muted',
            });
        }
      } else {
        const nodeId = row.id;
        const kwhText =
          row.usableKwh != null
            ? `${fmt(row.usableKwh)} kWh usable`
            : 'Usable kWh unknown';
        const textX = STRING_X + 46;
        const textRoom = STRING_RIGHT - textX - 4;
        const title =
          row.quantity > 1 ? `Battery · ${row.quantity} units` : 'Battery';
        const second = row.name
          ? `${kwhText} · ${solarShortProductName(row.name)}`
          : kwhText;
        // Combined units carry their own storage; a design battery needs its row verified.
        const verified = row.name == null || couplingVerified;
        const badge: Badge | null = verified
          ? null
          : { text: 'Unverified', tone: 'warn', width: 84, mark: 'bang' };
        const room = textRoom - (badge ? badge.width + 4 : 0);
        const lines = [fit(title, room, 13, true), fit(second, room, 12)];
        nodes.push({
          id: nodeId,
          kind: 'battery',
          x: STRING_X,
          y: cy - ROW_H / 2 + 4,
          width: STRING_W,
          height: ROW_H - 8,
          lines: verified ? lines : [...lines, UNVERIFIED],
          coupling: 'dc',
          couplingVerified: verified,
          usableKwh: row.usableKwh,
        });
        if (badge) badgeShapes(shapes, STRING_RIGHT - badge.width, cy, badge);
        addBatteryKwh(row.usableKwh);
        batterySymbol(shapes, STRING_X + 10, cy);
        shapes.push(
          {
            type: 'text',
            x: textX,
            y: cy - 4,
            text: lines[0],
            size: 13,
            weight: 'bold',
          },
          {
            type: 'text',
            x: textX,
            y: cy + 13,
            text: lines[1],
            size: 12,
            tone: 'muted',
          }
        );
        if (unitNodeId) {
          const points: [number, number][] = [
            [STRING_RIGHT + 6, cy],
            [INV_X, cy],
          ];
          edges.push({
            id: `dc:${nodeId}`,
            from: nodeId,
            to: unitNodeId,
            kind: 'dc',
            points,
            label: 'Battery',
          });
          edgeShapes.push({ type: 'polyline', points, strokeWidth: 1.5 });
          shapes.push({
            type: 'text',
            x: INV_X - 8,
            y: cy - 6,
            text: 'Battery',
            size: 12,
            anchor: 'end',
            tone: 'muted',
          });
        }
      }
    });

    if (unit && unitNodeId) {
      const selectionId = unit.equipmentSelectionId;
      const rawName = nameOf(selectionId);
      const acKw =
        numberSpec(selectionId, 'acOutputKw') ??
        (() => {
          const pv = spec(selectionId, 'pvConversion');
          return pv && known(pv.acOutputKw) ? pv.acOutputKw : null;
        })();
      const phasesRaw = spec(selectionId, 'phases');
      const phases: 1 | 3 = phasesRaw === 3 ? 3 : 1;
      const hybrid = spec(selectionId, 'hybrid') === true;
      const breaker = solarAcBreaker({
        region,
        acKw,
        phases,
        productName: rawName,
      });
      if (acKw == null) totals.acKw = null;
      else if (totals.acKw != null) totals.acKw += acKw;
      // A US name such as "480/277V" makes a unit three-phase.
      const shownPhases = breaker?.phases ?? phases;
      const rating = [
        acKw != null ? `${fmt(acKw)} kW AC` : 'kW AC unknown',
        shownPhases === 3 ? '3-phase' : '1-phase',
        ...(hybrid ? ['Hybrid'] : []),
      ].join(' · ');
      // The converter symbol sits bottom-right, clear of the three lines.
      const textRoom = INV_W - 20;
      const lines = [
        fit(unit.name, textRoom, 13, true),
        fit(
          rawName ? solarShortProductName(rawName) : 'Product unavailable',
          INV_W - 20,
          12
        ),
        fit(rating, INV_W - 20, 12),
      ];
      const cy = y + groupH / 2;
      nodes.push({
        id: unitNodeId,
        kind: 'inverter',
        x: INV_X,
        y,
        width: INV_W,
        height: groupH,
        lines,
        sourceId: unit.id,
        acKw,
        breaker,
      });
      shapes.push({
        type: 'rect',
        x: INV_X,
        y,
        width: INV_W,
        height: groupH,
        rx: 6,
        strokeWidth: 1.5,
      });
      inverterSymbol(shapes, INV_RIGHT - 40, y + groupH - 38);
      shapes.push(
        {
          type: 'text',
          x: INV_X + 10,
          y: y + 22,
          text: lines[0],
          size: 13,
          weight: 'bold',
        },
        {
          type: 'text',
          x: INV_X + 10,
          y: y + 40,
          text: lines[1],
          size: 12,
          tone: 'muted',
        },
        { type: 'text', x: INV_X + 10, y: y + 56, text: lines[2], size: 12 }
      );
      branches.push({
        sourceId: unitNodeId,
        y: cy,
        breaker,
        generation: true,
        title: unit.name,
      });
    }
    y += groupH + GROUP_GAP;
  }

  // ── AC-coupled battery: its own branch below the inverters ──
  if (acBattery) {
    const nodeId = `battery:${acBattery.applianceId}`;
    const kwh = known(acBattery.usableCapacityKwh)
      ? acBattery.usableCapacityKwh * batteryQty
      : null;
    const unitKw = known(acBattery.maxChargeDischargeKw)
      ? acBattery.maxChargeDischargeKw
      : (acBattery.acOutputKw ?? null);
    const kw = known(unitKw) ? unitKw * batteryQty : null;
    const phases: 1 | 3 = acBattery.phases === 3 ? 3 : 1;
    const breaker = solarAcBreaker({
      region,
      acKw: kw,
      phases,
      productName: acBattery.name,
    });
    const lines = [
      batteryQty > 1 ? `Battery · ${batteryQty} units` : 'Battery',
      // The battery symbol sits top-right beside the first two lines.
      fit(solarShortProductName(acBattery.name), INV_W - 60, 12),
      fit(
        [
          kwh != null ? `${fmt(kwh)} kWh usable` : 'Usable kWh unknown',
          ...(kw != null ? [`${fmt(kw)} kW`] : []),
        ].join(' · '),
        INV_W - 20,
        12
      ),
      ...(couplingVerified ? [] : [UNVERIFIED]),
    ];
    const boxH = AC_BATTERY_H + (couplingVerified ? 0 : 16);
    nodes.push({
      id: nodeId,
      kind: 'battery',
      x: INV_X,
      y,
      width: INV_W,
      height: boxH,
      lines,
      sourceId: acBattery.applianceId,
      coupling: 'ac',
      couplingVerified,
      usableKwh: kwh,
      acKw: kw,
      breaker,
    });
    addBatteryKwh(kwh);
    shapes.push({
      type: 'rect',
      x: INV_X,
      y,
      width: INV_W,
      height: boxH,
      rx: 6,
      strokeWidth: 1.5,
      ...(couplingVerified ? {} : { tone: 'warn' as const, dashed: true }),
    });
    if (!couplingVerified)
      shapes.push({
        type: 'text',
        x: INV_X + 10,
        y: y + 74,
        text: UNVERIFIED,
        size: 12,
        tone: 'warn',
      });
    batterySymbol(shapes, INV_RIGHT - 38, y + 22);
    shapes.push(
      {
        type: 'text',
        x: INV_X + 10,
        y: y + 22,
        text: lines[0],
        size: 13,
        weight: 'bold',
      },
      {
        type: 'text',
        x: INV_X + 10,
        y: y + 40,
        text: lines[1],
        size: 12,
        tone: 'muted',
      },
      { type: 'text', x: INV_X + 10, y: y + 56, text: lines[2], size: 12 }
    );
    branches.push({
      sourceId: nodeId,
      y: y + boxH / 2,
      breaker,
      generation: false,
      title: 'Battery',
    });
    y += boxH + GROUP_GAP;
  }

  // ── AC chain ──
  const contentBottom = Math.max(y - GROUP_GAP, PAD + 8);
  const isolatorLabel = region === 'uk' ? 'AC isolator' : 'AC disconnect';
  const cuLabel = region === 'uk' ? 'Consumer unit' : 'Main service panel';
  const cuNodeId = 'consumer-unit';
  const anyThreePhase = branches.some((branch) => branch.breaker?.phases === 3);
  branches.forEach((branch, index) => {
    const isoId = `isolator:${index + 1}`;
    nodes.push({
      id: isoId,
      kind: 'isolator',
      x: isoX - 20,
      y: branch.y - 14,
      width: 40,
      height: 28,
      lines: [isolatorLabel],
      sourceId: branch.sourceId,
    });
    isolatorSymbol(shapes, isoX, branch.y);
    shapes.push({
      type: 'text',
      x: isoX,
      y: branch.y + 28,
      text: isolatorLabel,
      size: 12,
      anchor: 'middle',
      tone: 'muted',
    });
    const current = branch.breaker ? `${fmt(branch.breaker.currentA)} A` : null;
    const outPoints: [number, number][] = [
      [INV_RIGHT, branch.y],
      [isoX - 20, branch.y],
    ];
    edges.push({
      id: `ac:${branch.sourceId}`,
      from: branch.sourceId,
      to: isoId,
      kind: 'ac',
      points: outPoints,
      label: current,
    });
    edgeShapes.push({ type: 'polyline', points: outPoints, strokeWidth: 2 });
    if (current)
      shapes.push({
        type: 'text',
        x: (INV_RIGHT + isoX - 20) / 2,
        y: branch.y - 6,
        text: current,
        size: 12,
        anchor: 'middle',
      });
    let fromId = isoId;
    let fromX = isoX + 20;
    if (region === 'uk' && branch.generation) {
      const genId = `generation-meter:${index + 1}`;
      nodes.push({
        id: genId,
        kind: 'generationMeter',
        x: genX - 18,
        y: branch.y - 18,
        width: 36,
        height: 36,
        lines: ['Generation meter'],
        sourceId: branch.sourceId,
      });
      meterSymbol(shapes, genX, branch.y);
      shapes.push({
        type: 'text',
        x: genX,
        y: branch.y + 34,
        text: 'Generation meter',
        size: 12,
        anchor: 'middle',
        tone: 'muted',
      });
      const points: [number, number][] = [
        [fromX, branch.y],
        [genX - 18, branch.y],
      ];
      edges.push({
        id: `ac:${isoId}`,
        from: isoId,
        to: genId,
        kind: 'ac',
        points,
        label: null,
      });
      edgeShapes.push({ type: 'polyline', points, strokeWidth: 2 });
      fromId = genId;
      fromX = genX + 18;
    }
    const points: [number, number][] = [
      [fromX, branch.y],
      [cuX + 10, branch.y],
    ];
    edges.push({
      id: `ac:${fromId}`,
      from: fromId,
      to: cuNodeId,
      kind: 'ac',
      points,
      label: breakerLabel(region, branch.breaker),
    });
    edgeShapes.push({ type: 'polyline', points, strokeWidth: 2 });
  });

  if (branches.length) {
    const top = Math.min(...branches.map((branch) => branch.y)) - 48;
    const bottom = Math.max(...branches.map((branch) => branch.y)) + 22;
    const cuY = Math.max(PAD, top);
    const cuH = Math.max(bottom - cuY, 72);
    const busX = cuRight - 22;
    const ys = branches.map((branch) => branch.y);
    const midY = (Math.min(...ys) + Math.max(...ys)) / 2;
    nodes.push({
      id: cuNodeId,
      kind: 'consumerUnit',
      x: cuX,
      y: cuY,
      width: CU_W,
      height: cuH,
      lines: [
        cuLabel,
        ...branches.map((branch) => breakerLabel(region, branch.breaker)),
      ],
    });
    shapes.push(
      {
        type: 'rect',
        x: cuX,
        y: cuY,
        width: CU_W,
        height: cuH,
        rx: 6,
        strokeWidth: 1.5,
      },
      {
        type: 'text',
        x: cuX + 10,
        y: cuY + 20,
        text: fit(cuLabel, CU_W - 20, 13, true),
        size: 13,
        weight: 'bold',
      }
    );
    const busTop = Math.min(midY, ...branches.map((branch) => branch.y));
    const busBottom = Math.max(midY, ...branches.map((branch) => branch.y));
    shapes.push({
      type: 'line',
      x1: busX,
      y1: busTop,
      x2: busX,
      y2: busBottom,
      strokeWidth: 3,
    });
    for (const branch of branches) {
      breakerSymbol(shapes, cuX + 10, branch.y);
      shapes.push(
        {
          type: 'line',
          x1: cuX + 26,
          y1: branch.y,
          x2: busX,
          y2: branch.y,
          strokeWidth: 1.5,
        },
        {
          type: 'text',
          x: cuX + 32,
          y: branch.y - 6,
          text: fit(breakerLabel(region, branch.breaker), busX - cuX - 36, 12),
          size: 12,
        }
      );
    }
    // Main way out of the board.
    const meterId = 'grid-meter';
    const gridId = 'grid';
    const meterLabel = region === 'uk' ? 'Grid meter' : 'Utility meter';
    const threePhaseVolts = Math.max(
      ...branches.map((branch) =>
        branch.breaker?.phases === 3 ? branch.breaker.voltageV : 0
      )
    );
    let supply = region === 'uk' ? '230 V 1-phase' : '120/240 V split-phase';
    if (anyThreePhase)
      supply = `${region === 'uk' ? 400 : threePhaseVolts} V 3-phase`;
    const gridLabel = region === 'uk' ? 'Grid (DNO)' : 'Utility';
    nodes.push(
      {
        id: meterId,
        kind: 'gridMeter',
        x: meterX - 18,
        y: midY - 18,
        width: 36,
        height: 36,
        lines: [meterLabel],
      },
      {
        id: gridId,
        kind: 'grid',
        x: gridX - 18,
        y: midY - 20,
        width: 36,
        height: 40,
        lines: [gridLabel, supply],
      }
    );
    const toMeter: [number, number][] = [
      [busX, midY],
      [meterX - 18, midY],
    ];
    const toGrid: [number, number][] = [
      [meterX + 18, midY],
      [gridX - 18, midY],
    ];
    edges.push(
      {
        id: 'ac:consumer-unit',
        from: cuNodeId,
        to: meterId,
        kind: 'ac',
        points: toMeter,
        label: null,
      },
      {
        id: 'ac:grid-meter',
        from: meterId,
        to: gridId,
        kind: 'ac',
        points: toGrid,
        label: null,
      }
    );
    edgeShapes.push(
      { type: 'polyline', points: toMeter, strokeWidth: 2 },
      { type: 'polyline', points: toGrid, strokeWidth: 2 }
    );
    meterSymbol(shapes, meterX, midY);
    gridSymbol(shapes, gridX, midY);
    shapes.push(
      {
        type: 'text',
        x: meterX,
        y: midY + 34,
        text: meterLabel,
        size: 12,
        anchor: 'middle',
        tone: 'muted',
      },
      {
        type: 'text',
        x: gridX,
        y: midY + 38,
        text: gridLabel,
        size: 13,
        weight: 'bold',
        anchor: 'middle',
      },
      {
        type: 'text',
        x: gridX,
        y: midY + 54,
        text: supply,
        size: 12,
        anchor: 'middle',
        tone: 'muted',
      }
    );
  }

  // ── Footer totals ──
  const lastY = Math.max(
    contentBottom,
    ...nodes.map(
      (node) => node.y + node.height + (node.kind === 'grid' ? 40 : 20)
    )
  );
  const footerH = FOOTER_H + (region === 'us' ? FOOTER_NOTE_H : 0);
  const height = Math.ceil(lastY + footerH);
  const totalsLine = [
    plural(totals.panels, 'panel'),
    ...(totals.dcKw != null && totals.panels
      ? [`${fmt(totals.dcKw, 2)} kWp DC`]
      : []),
    ...(totals.acKw != null && totals.inverters
      ? [`${fmt(totals.acKw)} kW AC`]
      : []),
    plural(totals.inverters, 'inverter'),
    plural(totals.strings, 'string'),
    ...(totals.batteries
      ? [
          totals.batteryKwh != null
            ? `${fmt(totals.batteryKwh)} kWh storage`
            : plural(totals.batteries, 'battery', 'batteries'),
        ]
      : []),
  ].join(' · ');
  shapes.push(
    {
      type: 'line',
      x1: PAD,
      y1: height - footerH + 8,
      x2: width - PAD,
      y2: height - footerH + 8,
      tone: 'muted',
      strokeWidth: 1,
    },
    {
      type: 'text',
      x: PAD,
      y: height - 14 - (footerH - FOOTER_H),
      text: totalsLine,
      size: 12,
      tone: 'muted',
    },
    ...(region === 'us'
      ? [
          {
            type: 'text' as const,
            x: PAD,
            y: height - 14,
            text: US_BUSBAR_NOTE,
            size: 12 as const,
            tone: 'muted' as const,
          },
        ]
      : [])
  );

  const summary = [
    plural(totals.inverters, 'inverter'),
    plural(totals.strings, 'string'),
    plural(totals.panels, 'panel'),
    ...(totals.batteries
      ? [plural(totals.batteries, 'battery', 'batteries')]
      : []),
  ].join(', ');

  return {
    region,
    width,
    height,
    nodes,
    edges,
    shapes: [...edgeShapes, ...shapes],
    totals: {
      ...totals,
      dcKw: totals.dcKw != null ? round(totals.dcKw, 3) : null,
      acKw: totals.acKw != null ? round(totals.acKw, 3) : null,
      batteryKwh:
        totals.batteryKwh != null ? round(totals.batteryKwh, 3) : null,
    },
    summary,
  };
};

const breakerLabel = (
  region: SolarDiagramRegion,
  breaker: SolarAcBreaker | null
) => {
  if (!breaker)
    return region === 'uk' ? 'MCB size unknown' : 'Breaker size unknown';
  const sizes = SOLAR_AC_BREAKER_SIZES_A[region];
  const max = sizes[sizes.length - 1];
  const size =
    breaker.breakerA != null ? `${breaker.breakerA} A` : `Over ${max} A`;
  if (region === 'uk')
    return `${size} ${breaker.phases === 3 ? 'TP MCB' : 'MCB'}`;
  return `${size} ${breaker.phases === 3 ? '3-pole' : '2-pole'}`;
};

// ── Symbols (IEC 60617-style, drawn in currentColor) ─────────────────────

type Badge = {
  text: string;
  tone: SolarDiagramTone;
  width: number;
  mark: 'tick' | 'bang' | null;
};

const badgeOf = (check: SolarDiagramStringCheck): Badge | null => {
  switch (check) {
    case 'ok':
      return { text: 'OK', tone: 'ok', width: 42, mark: 'tick' };
    case 'check':
      return { text: 'Check', tone: 'warn', width: 60, mark: 'bang' };
    case 'review':
      return { text: 'Review', tone: 'warn', width: 64, mark: 'bang' };
    case 'unconnected':
      return { text: 'Not connected', tone: 'muted', width: 96, mark: null };
    default:
      return null;
  }
};

const badgeShapes = (
  out: SolarDiagramShape[],
  x: number,
  cy: number,
  badge: Badge
) => {
  out.push({
    type: 'rect',
    x,
    y: cy - 10,
    width: badge.width,
    height: 20,
    rx: 10,
    tone: badge.tone,
    strokeWidth: 1,
  });
  const textX = badge.mark ? x + 20 : x + 8;
  if (badge.mark === 'tick')
    out.push({
      type: 'path',
      d: `M${x + 7} ${cy}l3 3l6 -6`,
      tone: badge.tone,
      strokeWidth: 1.5,
    });
  if (badge.mark === 'bang')
    out.push(
      {
        type: 'line',
        x1: x + 11,
        y1: cy - 5,
        x2: x + 11,
        y2: cy + 1,
        tone: badge.tone,
        strokeWidth: 1.75,
      },
      {
        type: 'circle',
        cx: x + 11,
        cy: cy + 4.5,
        r: 1,
        tone: badge.tone,
        fill: 'current',
        stroke: false,
      }
    );
  out.push({
    type: 'text',
    x: textX,
    y: cy + 4,
    text: badge.text,
    size: 12,
    tone: badge.tone,
  });
};

/** Colour bar plus a module outline with a cell grid. */
const moduleSymbol = (
  out: SolarDiagramShape[],
  x: number,
  cy: number,
  colour: string | undefined,
  dashed: boolean
) => {
  if (colour)
    out.push({
      type: 'rect',
      x,
      y: cy - 16,
      width: 5,
      height: 32,
      rx: 2,
      fill: colour,
      stroke: false,
    });
  const mx = x + 12;
  out.push(
    ...(colour
      ? [
          {
            type: 'rect' as const,
            x: mx,
            y: cy - 14,
            width: 26,
            height: 28,
            rx: 2,
            fill: colour,
            fillOpacity: 0.18,
            stroke: false,
          },
        ]
      : []),
    {
      type: 'rect',
      x: mx,
      y: cy - 14,
      width: 26,
      height: 28,
      rx: 2,
      strokeWidth: 1.5,
      dashed,
    },
    {
      type: 'line',
      x1: mx + 13,
      y1: cy - 14,
      x2: mx + 13,
      y2: cy + 14,
      strokeWidth: 1,
    },
    {
      type: 'line',
      x1: mx,
      y1: cy - 4.67,
      x2: mx + 26,
      y2: cy - 4.67,
      strokeWidth: 1,
    },
    {
      type: 'line',
      x1: mx,
      y1: cy + 4.67,
      x2: mx + 26,
      y2: cy + 4.67,
      strokeWidth: 1,
    }
  );
};

/** Two cells: long plate (+), short heavy plate (−). */
const batterySymbol = (out: SolarDiagramShape[], x: number, cy: number) => {
  const left = x + 4;
  out.push(
    {
      type: 'line',
      x1: left,
      y1: cy - 12,
      x2: left,
      y2: cy + 12,
      strokeWidth: 1.5,
    },
    {
      type: 'line',
      x1: left + 6,
      y1: cy - 6,
      x2: left + 6,
      y2: cy + 6,
      strokeWidth: 3.5,
    },
    {
      type: 'line',
      x1: left + 14,
      y1: cy - 12,
      x2: left + 14,
      y2: cy + 12,
      strokeWidth: 1.5,
    },
    {
      type: 'line',
      x1: left + 20,
      y1: cy - 6,
      x2: left + 20,
      y2: cy + 6,
      strokeWidth: 3.5,
    },
    {
      type: 'line',
      x1: left + 6,
      y1: cy,
      x2: left + 14,
      y2: cy,
      strokeWidth: 1,
      dashed: true,
    }
  );
};

/** Converter: box split by a diagonal, "=" in the DC corner, "~" in the AC corner. */
const inverterSymbol = (out: SolarDiagramShape[], x: number, y: number) => {
  out.push(
    { type: 'rect', x, y, width: 30, height: 30, rx: 2, strokeWidth: 1.25 },
    { type: 'line', x1: x, y1: y + 30, x2: x + 30, y2: y, strokeWidth: 1 },
    {
      type: 'line',
      x1: x + 4,
      y1: y + 7,
      x2: x + 13,
      y2: y + 7,
      strokeWidth: 1.25,
    },
    {
      type: 'line',
      x1: x + 4,
      y1: y + 11,
      x2: x + 13,
      y2: y + 11,
      strokeWidth: 1.25,
    },
    {
      type: 'path',
      d: `M${x + 16} ${y + 23}q2.5 -4 5 0t5 0`,
      strokeWidth: 1.25,
    }
  );
};

/** Open switch: two contacts and a raised blade. */
const isolatorSymbol = (
  out: SolarDiagramShape[],
  cx: number,
  cy: number,
  lead = 2
) => {
  out.push(
    {
      type: 'line',
      x1: cx - 20,
      y1: cy,
      x2: cx - 12,
      y2: cy,
      strokeWidth: lead,
    },
    { type: 'circle', cx: cx - 10, cy, r: 2.5, strokeWidth: 1.5 },
    {
      type: 'line',
      x1: cx - 8,
      y1: cy - 1,
      x2: cx + 10,
      y2: cy - 11,
      strokeWidth: lead,
    },
    { type: 'circle', cx: cx + 10, cy, r: 2.5, strokeWidth: 1.5 },
    {
      type: 'line',
      x1: cx + 12,
      y1: cy,
      x2: cx + 20,
      y2: cy,
      strokeWidth: lead,
    }
  );
};

const meterSymbol = (out: SolarDiagramShape[], cx: number, cy: number) => {
  out.push(
    { type: 'circle', cx, cy, r: 18, strokeWidth: 1.5 },
    { type: 'text', x: cx, y: cy + 4, text: 'kWh', size: 12, anchor: 'middle' }
  );
};

/** Breaker: a square with a trip diagonal, wired from its left edge. */
const breakerSymbol = (out: SolarDiagramShape[], x: number, cy: number) => {
  out.push(
    {
      type: 'rect',
      x,
      y: cy - 7,
      width: 16,
      height: 14,
      rx: 1,
      strokeWidth: 1.25,
    },
    { type: 'path', d: `M${x + 3} ${cy + 4}q5 -11 10 -8`, strokeWidth: 1.25 }
  );
};

/** Lattice pylon. */
const gridSymbol = (out: SolarDiagramShape[], cx: number, cy: number) => {
  out.push(
    { type: 'line', x1: cx - 18, y1: cy, x2: cx - 9, y2: cy, strokeWidth: 2 },
    {
      type: 'path',
      d: `M${cx - 9} ${cy + 20}L${cx} ${cy - 20}L${cx + 9} ${cy + 20}M${cx - 12} ${cy - 10}H${cx + 12}M${cx - 9} ${cy - 2}H${cx + 9}M${cx - 6} ${cy + 8}L${cx + 4.5} ${cy}M${cx + 6} ${cy + 8}L${cx - 4.5} ${cy}`,
      strokeWidth: 1.5,
    }
  );
};

// ── Standalone SVG ─────────────────────────────────────────────────────────

const TONE_COLOUR: Record<SolarDiagramTone, string> = {
  default: '#1f2937',
  muted: '#6b7280',
  ok: '#15803d',
  warn: '#b45309',
};

const escapeXml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

const attrs = (values: Record<string, string | number | undefined>) =>
  Object.entries(values)
    .filter(([, value]) => value !== undefined)
    .map(([key, value]) => `${key}="${escapeXml(String(value))}"`)
    .join(' ');

/** Presentation attributes shared by the SVG serialiser and the React renderer. */
export const solarDiagramPaint = (
  shape: Exclude<SolarDiagramShape, { type: 'text' }>,
  colourOf: (tone: SolarDiagramTone) => string
) => {
  const colour = colourOf(shape.tone ?? 'default');
  return {
    stroke: shape.stroke === false ? 'none' : colour,
    fill: shape.fill === 'current' ? colour : (shape.fill ?? 'none'),
    fillOpacity: shape.fillOpacity,
    strokeWidth:
      shape.stroke === false ? undefined : (shape.strokeWidth ?? 1.5),
    strokeDasharray: shape.dashed ? '4 3' : undefined,
  };
};

/** Standalone SVG document text, light background, for download or documents. */
export const solarSingleLineDiagramSvg = (
  diagram: SolarSingleLineDiagram,
  options: { title?: string } = {}
) => {
  const colourOf = (tone: SolarDiagramTone) => TONE_COLOUR[tone];
  const body = diagram.shapes
    .map((shape) => {
      if (shape.type === 'text')
        return `<text ${attrs({
          x: shape.x,
          y: shape.y,
          'font-size': shape.size,
          'font-weight': shape.weight === 'bold' ? 600 : undefined,
          'text-anchor':
            shape.anchor && shape.anchor !== 'start' ? shape.anchor : undefined,
          fill: colourOf(shape.tone ?? 'default'),
        })}>${escapeXml(shape.text)}</text>`;
      const paint = solarDiagramPaint(shape, colourOf);
      const common = {
        stroke: paint.stroke,
        fill: paint.fill,
        'fill-opacity': paint.fillOpacity,
        'stroke-width': paint.strokeWidth,
        'stroke-dasharray': paint.strokeDasharray,
      };
      switch (shape.type) {
        case 'line':
          return `<line ${attrs({ x1: shape.x1, y1: shape.y1, x2: shape.x2, y2: shape.y2, ...common })}/>`;
        case 'rect':
          return `<rect ${attrs({ x: shape.x, y: shape.y, width: shape.width, height: shape.height, rx: shape.rx, ...common })}/>`;
        case 'circle':
          return `<circle ${attrs({ cx: shape.cx, cy: shape.cy, r: shape.r, ...common })}/>`;
        case 'path':
          return `<path ${attrs({ d: shape.d, ...common })}/>`;
        case 'polyline':
          return `<polyline ${attrs({ points: shape.points.map((point) => point.join(',')).join(' '), ...common })}/>`;
      }
    })
    .join('');
  const title = options.title ?? 'Single-line diagram';
  return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" ${attrs({
    width: diagram.width,
    height: diagram.height,
    viewBox: `0 0 ${diagram.width} ${diagram.height}`,
    role: 'img',
    'aria-label': diagram.summary,
    'font-family':
      'Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
    'stroke-linecap': 'round',
    'stroke-linejoin': 'round',
  })}><title>${escapeXml(`${title}: ${diagram.summary}`)}</title><rect width="100%" height="100%" fill="#ffffff"/>${body}</svg>
`;
};
